import { Component, createEffect, createMemo, createSignal, For, onMount, Show } from 'solid-js'
import { characterStore, i18nStore } from '../../store'
import { getAssetUrl } from '../../shared/util'
import { A } from '@solidjs/router'
import { MessageSquare, Heart, Search, Tag, ChevronDown, Filter, X } from 'lucide-solid'
import { CharacterDetailModal } from '../Character/components/CharacterDetailModal'
import Button from '../../shared/Button'
import Modal from '../../shared/Modal'

const DiscoveryPage: Component = () => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const [chars, setChars] = createSignal<any[]>([])
  const [loading, setLoading] = createSignal(true)
  const [activeTab, setActiveTab] = createSignal('latest')
  const [searchQuery, setSearchQuery] = createSignal('')
  const [selectedTag, setSelectedTag] = createSignal('')
  const [showDetail, setShowDetail] = createSignal<any>()

  // 新增过滤状态
  const [showFilters, setShowFilters] = createSignal(false)
  const [selectedGender, setSelectedGender] = createSignal('all')
  const [selectedTags, setSelectedTags] = createSignal<string[]>([])
  const [availableTags, setAvailableTags] = createSignal<string[]>([])
  const [showTagModal, setShowTagModal] = createSignal(false)

  const fetchCharacters = async (isInitial = false) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (selectedGender() !== 'all') params.append('gender', selectedGender())
      if (selectedTags().length > 0) params.append('tags', selectedTags().join(','))
      if (searchQuery()) params.append('search', searchQuery())
      params.append('visibility', 'public')

      const endpoint = isInitial ? '/api/character/discovery' : '/api/character'
      const res = await fetch(`${endpoint}?${params.toString()}`)
      const data = await res.json()
      
      setChars(data.characters || [])
      if (isInitial && data.tags) {
        setAvailableTags(data.tags)
      }
    } catch (e) {
      console.error('Failed to fetch discovery data', e)
    } finally {
      setLoading(false)
    }
  }

  onMount(async () => {
    fetchCharacters(true)
  })

  // 当过滤条件变化时重新抓取数据
  createEffect(() => {
    fetchCharacters()
  })

  const allTags = createMemo(() => {
    const tags = new Set<string>()
    chars().forEach((c) => c.tags?.forEach((t: string) => tags.add(t)))
    return Array.from(tags).slice(0, 10)
  })

  const filteredChars = createMemo(() => {
    let list = [...chars()]

    if (activeTab() === 'latest') {
      return list.sort(
        (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      )
    }
    return list.sort(() => Math.random() - 0.5)
  })

  return (
    <div class="flex flex-col gap-4 p-4">
      {/* Tabs */}
      <div class="flex gap-6 border-b border-[var(--bg-700)] pb-2 overflow-x-auto no-scrollbar">
        <TabItem 
          active={activeTab() === 'latest'} 
          onClick={() => setActiveTab('latest')}
          label={i18n.t.home_tab_latest} 
        />
        <TabItem 
          active={activeTab() === 'follow'} 
          onClick={() => setActiveTab('follow')}
          label={i18n.t.trends_follow} 
        />
        <TabItem 
          active={activeTab() === 'trending'} 
          onClick={() => setActiveTab('trending')}
          label={i18n.t.home_tab_trending} 
        />
        <TabItem 
          active={activeTab() === 'race'} 
          onClick={() => setActiveTab('race')}
          label={i18n.t.home_tab_race} 
        />
        <TabItem 
          active={activeTab() === 'recommended'} 
          onClick={() => setActiveTab('recommended')}
          label={i18n.t.home_tab_recommended} 
        />
      </div>

      {/* Search Bar and Filter Icon */}
      <div class="flex items-center gap-2">
        <div class="relative flex-1">
          <div class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-[var(--bg-400)]">
            <Search size={18} />
          </div>
          <input
            type="text"
            placeholder={i18n.t.search_placeholder}
            class="w-full rounded-full bg-[var(--bg-800)] border border-[var(--bg-700)] py-2 pl-10 pr-4 text-sm focus:border-[var(--hl-500)] focus:outline-none transition-all"
            value={searchQuery()}
            onInput={(e) => setSearchQuery(e.currentTarget.value)}
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters())}
          class="p-2 rounded-full text-white shadow-lg transition-all"
          style={{
            background: showFilters()
              ? 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
          }}
        >
          <Filter size={20} />
        </button>
      </div>

      {/* Gender and Tag Filters */}
      <Show when={showFilters()}>
        <div class="flex flex-col gap-3">
          <div class="flex gap-2">
            <button
              onClick={() =>
                setSelectedGender(
                  selectedGender() === 'all'
                    ? 'female'
                    : selectedGender() === 'female'
                    ? 'male'
                    : 'all'
                )
              }
              class="flex items-center justify-between gap-2 px-4 py-2 rounded-xl bg-[var(--bg-800)] border border-[var(--bg-700)] text-sm min-w-[80px]"
            >
              <span>
                {selectedGender() === 'all' ? '全部' : selectedGender() === 'male' ? '男' : '女'}
              </span>
              <ChevronDown size={14} />
            </button>
            <button
              onClick={() => setShowTagModal(true)}
              class="flex flex-1 items-center gap-2 px-4 py-2 rounded-xl bg-[var(--bg-800)] border border-[var(--bg-700)] text-sm text-[var(--bg-400)]"
            >
              <Tag size={14} />
              <span>
                {selectedTags().length > 0
                  ? `已选 ${selectedTags().length} 个标签`
                  : i18n.t.home_filter_tags}
              </span>
            </button>
          </div>

          <Show when={selectedTags().length > 0}>
            <div class="flex flex-wrap gap-2">
              <For each={selectedTags()}>
                {(tag) => (
                  <span class="bg-[var(--bg-700)] flex items-center gap-1 rounded-full px-2 py-1 text-[10px] text-white/80">
                    {tag}
                    <X
                      size={10}
                      class="cursor-pointer hover:text-white"
                      onClick={() => setSelectedTags(selectedTags().filter((t) => t !== tag))}
                    />
                  </span>
                )}
              </For>
            </div>
          </Show>
        </div>
      </Show>

      {/* Masonry-like Grid */}
      <div class="columns-2 gap-3 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-6">
        <For each={filteredChars()}>
          {(char) => (
            <div class="mb-3 break-inside-avoid">
              <CharacterDiscoveryCard char={char} onClick={() => setShowDetail(char)} />
            </div>
          )}
        </For>
      </div>

      <CharacterDetailModal
        show={!!showDetail()}
        close={() => setShowDetail()}
        char={showDetail() || {}}
      />

      <TagModal
        show={showTagModal()}
        close={() => setShowTagModal(false)}
        availableTags={availableTags()}
        selectedTags={selectedTags()}
        onConfirm={(tags) => {
          setSelectedTags(tags)
          setShowTagModal(false)
        }}
      />
    </div>
  )
}

const TagModal: Component<{
  show: boolean
  close: () => void
  availableTags: string[]
  selectedTags: string[]
  onConfirm: (tags: string[]) => void
}> = (props) => {
  const [tempSelected, setTempSelected] = createSignal<string[]>([...props.selectedTags])

  const toggleTag = (tag: string) => {
    if (tempSelected().includes(tag)) {
      setTempSelected(tempSelected().filter((t) => t !== tag))
    } else {
      setTempSelected([...tempSelected(), tag])
    }
  }

  return (
    <Modal title="选择标签" show={props.show} close={props.close} maxWidth="small">
      <div class="flex flex-col gap-4 p-4">
        <div class="flex max-h-[60vh] flex-wrap gap-2 overflow-y-auto no-scrollbar">
          <For each={props.availableTags}>
            {(tag) => (
              <button
                onClick={() => toggleTag(tag)}
                class="rounded-full px-3 py-1 text-sm transition-colors"
                classList={{
                  'bg-[var(--hl-500)] text-white': tempSelected().includes(tag),
                  'bg-[var(--bg-700)] hover:bg-[var(--bg-600)] text-[var(--bg-100)]':
                    !tempSelected().includes(tag),
                }}
              >
                {tag}
              </button>
            )}
          </For>
        </div>
        <div class="mt-4">
          <Button class="w-full" schema="primary" onClick={() => props.onConfirm(tempSelected())}>
            确认
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const TabItem: Component<{ active: boolean; label: string; onClick: () => void }> = (props) => (
  <button 
    onClick={props.onClick}
    class="relative pb-2 text-sm font-medium transition-colors whitespace-nowrap"
    classList={{
      'text-white': props.active,
      'text-[var(--bg-400)] hover:text-[var(--bg-100)]': !props.active
    }}
  >
    {props.label}
    <Show when={props.active}>
      <div class="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" />
    </Show>
  </button>
)

const CharacterDiscoveryCard: Component<{ char: any; onClick: () => void }> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))

  const formatCount = (num: number) => {
    if (!num) return '0'
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k'
    }
    return num.toString()
  }

  return (
    <div
      onClick={props.onClick}
      class="group relative block overflow-hidden rounded-2xl bg-[var(--bg-800)] transition-transform hover:scale-[1.02] border border-[var(--bg-700)] cursor-pointer"
    >
      <div class="relative aspect-[3/4] w-full overflow-hidden">
        <img
          src={getAssetUrl(props.char.avatar || '')}
          alt={props.char.name}
          class="h-full w-full object-cover"
          loading="lazy"
        />
        {/* Original Badge */}
        <Show when={props.char.adminType && props.char.adminType !== 'none'}>
          <div
            class="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-bold text-white backdrop-blur-sm"
            style={{
              background:
                props.char.adminType === 'original'
                  ? 'rgba(59, 130, 246, 0.8)'
                  : 'rgba(16, 185, 129, 0.8)',
            }}
          >
            {props.char.adminType === 'original' ? '原创' : '改进'}
          </div>
        </Show>
        {/* Interaction Count Badge */}
        <div class="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/40 text-[10px] font-bold text-white backdrop-blur-sm flex items-center gap-1">
          <Search size={10} />
          {formatCount(props.char.chatCount)}
        </div>

        {/* Bottom Info Overlay */}
        <div class="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
          <div class="text-sm font-bold text-white truncate">{props.char.name}</div>
          <div class="mt-1 text-[10px] text-white/70 line-clamp-2 leading-tight">
            {props.char.description || '...'}
          </div>

          <div class="mt-2 flex items-center justify-between">
            <div class="text-[10px] text-white/60 truncate max-w-[50%]" title={props.char.creatorName || props.char.creator || 'User'}>
              @{props.char.creatorName || props.char.creator || 'User'}
            </div>
            <div class="flex items-center gap-2 text-[10px] text-white/80">
              <span class="flex items-center gap-0.5">
                <MessageSquare size={10} /> {formatCount(props.char.commentCount)}
              </span>
              <span class="flex items-center gap-0.5">
                <Heart size={10} /> {formatCount(props.char.favoriteCount)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DiscoveryPage
